import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {ImageResource} from '@services/resource/image-resource';
import {ProductResource} from '@services/resource/product-resource';
import {ProductService} from '@services/product.service';
import {ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {UploadDocument} from '@services/resource/upload-document';
import {delay, switchMap, tap} from 'rxjs/operators';
import {StartupService} from '@services/startup.service';
import {LinkType} from '@services/resource/link-type';
import {Startup} from '@services/model/startup';
import {ImageType} from '@services/resource/image-type';
import {UploadImageComponent} from '@dashboard/upload-image/upload-image.component';
import {merge, Observable, of, Subject} from 'rxjs';
import {VoilaUserService} from '@services/voila-user.service';

import {ActivatedRoute} from '@angular/router';
import {ContinuousActionComponent} from '@widgets/continuous-action/continuous-action.component';
import {Product} from '@services/model/product';
import {ImageUploadService} from '@services/image-upload.service';
import {isSome, Option} from 'fp-ts/lib/Option';
import {productCategories, ProductCategory} from '@app/product-group-select/product-group-select.component';
import {AvailableParamsResource} from '@services/resource/available-params-resource';
import {SearchproductService} from '@services/searchproduct.service';
import {IImage} from '@services/resource/iimage';
import {MarkantElementsModule} from '@markant/elements-angular';
import {PitchDeckComponent} from '@dashboard/pitch-deck/pitch-deck.component';
import {UploadDocumentComponent} from '@dashboard/upload-document/upload-document.component';
import {MatCardContent} from '@angular/material/card';
import {MediaListComponent} from '@dashboard/media-list/media-list.component';
import {CommonModule} from '@angular/common';
import {LinkHttpPipe} from '@util/pipes/http-link-pipe';
import {MatTooltipModule} from '@angular/material/tooltip';

export const PRODUCT_DESC_LABEL = 'Was kannst Du über Dein Produkt erzählen?';
export const PRODUCT_NAME_MAX_LENGTH = 35;

@Component({
  selector: 'app-product-editor',
  templateUrl: './product-editor.component.html',
  styleUrls: ['./product-editor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContinuousActionComponent,
    MarkantElementsModule,
    PitchDeckComponent,
    UploadDocumentComponent,
    MatCardContent,
    MediaListComponent,
    ReactiveFormsModule,
    LinkHttpPipe,
    MatTooltipModule,
  ],
})
export class ProductEditorComponent implements OnInit {
  @Input() allowEmpty = false;
  @Input() availableOnly = false;

  externalEdit = false;

  LinkType = LinkType;
  Startup = Startup;

  uuid: string;
  startup: Startup;
  docs: UploadDocument[];
  images: ImageResource[];
  images$: Observable<IImage[]>;
  product: Product;
  coverImage: ImageResource | undefined;
  pitchDeck: UploadDocument | undefined;
  productCategories: ProductCategory[] = productCategories;
  imageType = ImageType.COVER;
  transmittedImage: File | undefined = undefined;

  @ViewChild(UploadImageComponent, {static: false}) coverImageUpload: UploadImageComponent;

  @ViewChild('continuousProduct', {static: true}) continuousProduct: ContinuousActionComponent;
  @ViewChild('continuousCoverImage') continuousCoverImage: ContinuousActionComponent;

  private readonly childChanges$: Subject<void> = new Subject();

  public editable?: boolean;

  readonly nameMaxLength = PRODUCT_NAME_MAX_LENGTH;

  productForm = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.maxLength(PRODUCT_NAME_MAX_LENGTH)]),
    claim: new UntypedFormControl(''),
    description: new UntypedFormControl(''),
    price: new UntypedFormControl(''),
    listed: new UntypedFormControl(''),
    packingUnit: new UntypedFormControl(''),
    productGroup: new UntypedFormControl(''),
    documents: new UntypedFormControl([]),
  });

  constructor(
    private readonly productService: ProductService,
    private readonly startupService: StartupService,
    private readonly imageUploadService: ImageUploadService,
    private readonly userService: VoilaUserService,
    private readonly route: ActivatedRoute,
    private readonly searchProductService: SearchproductService,
  ) {}

  ngOnInit() {
    if (this.availableOnly) {
      this.searchProductService.getAvailableParams().subscribe((ps) => this.filterProductGroups(ps));
    }
    this.productForm.disable();

    const productId = this.route.snapshot.paramMap.get('productId');
    let request;
    if (productId) {
      /* MA hacky way */
      request = this.productService.getProduct(productId);
      this.externalEdit = true;
    } else {
      /* old way, go through user */
      request = this.productService.productForUser(this.userService.currentUser().id);
    }

    request
      .pipe(
        switchMap((p: Product) => {
          this.setProduct(p);
          return this.productService.getIsEditable(p.id as string);
        }),
      )
      .subscribe((status) => {
        this.editable = status;
        if (this.editable === true) {
          this.productForm.enable();
        }
      });

    const changes$ = merge(this.productForm.valueChanges, this.childChanges$).pipe(delay(1000)); // magic number
    changes$.pipe(this.continuousProduct.actions(() => this.postProduct())).subscribe();
  }

  private setProduct(p: Product): void {
    this.product = p;
    console.log(p.name);
    this.productForm.setValue({
      name: p.name,
      claim: p.claim || '',
      description: p.description,
      price: p.uvp || '',
      listed: p.listed || '',
      packingUnit: p.packingUnit || '',
      productGroup: p.productGroup,
      documents: p.documents ? p.documents : [],
    });
    console.log(this.productForm.get('name')?.value);
    this.images = p.productImages;
    this.images$ = of(this.images.map((i) => ({url: i.uri}) as IImage));
    this.coverImage = p.coverImage;
    this.pitchDeck = p.pitchDeck;
    this.docs = p.documents || [];
    this.product = p;
    this.startupService.read(this.product.startupId).subscribe((s) => (this.startup = Startup.fromResource(s)));
  }

  private postProduct(): Observable<Product> {
    let price = this.productForm.controls.price.value;
    price = price ? (price as string).replace(/,/g, '.') : null;
    const productResource = new ProductResource(
      this.product.id,
      this.product.startupId,
      this.productForm.controls.name.value,
      this.productForm.controls.claim.value,
      this.productForm.controls.description.value,
      this.productForm.controls.listed.value,
      price,
      this.productForm.controls.productGroup.value,
      this.productForm.controls.packingUnit.value,
      this.coverImage,
      this.images,
      this.pitchDeck,
      this.docs,
      this.product.pitchVideo,
      null,
      undefined,
    );
    return this.productService.saveProduct(productResource).pipe(tap((p) => (this.product = p)));
  }

  updateDocs(docs: UploadDocument[]) {
    this.docs = docs;
    this.childChanges$.next();
  }

  updateMedia(mediaList: ImageResource[]) {
    this.images = mediaList;
    this.images$ = of(this.images.map((i) => ({url: i.uri}) as IImage));
    this.childChanges$.next();
  }

  pitchDeckChanged(change: Option<UploadDocument>) {
    if (isSome(change)) {
      this.pitchDeck = change.value;
    } else {
      console.log('deleting pitch deck');
      this.pitchDeck = undefined;
    }
    this.childChanges$.next();
  }

  showEditCoverPopup(img: [Blob, string]): void {
    this.deleteCoverImage();
    if (!this.editable) {
      return;
    }
    this.imageUploadService
      .uploadImage(this.product.id as string, img[0], new ImageResource(ImageType.COVER, undefined, undefined, undefined, img[1]))
      .subscribe((imgResource) => {
        this.transmittedImage = undefined;
        this.coverImage = imgResource;
        this.childChanges$.next();
      });
  }

  deleteCoverImage(): void {
    this.coverImage = undefined;
    this.transmittedImage = undefined;
    this.childChanges$.next();
  }

  private filterProductGroups(ps: AvailableParamsResource): void {
    this.productCategories = this.productCategories.map((c) => {
      return {...c, productGroups: c.productGroups.filter((g) => ps.productGroups.indexOf(g.value) >= 0)};
    });
  }
}
